import { t } from '@lingui/core/macro';
import { HttpError } from '@luminovo/api-contracts';
import { ACCESS_TOKEN_QUERY_KEY } from '@luminovo/auth';
import { isProductionEnvironment, logToExternalErrorHandlers } from '@luminovo/commons';
import { Flexbox, PrimaryButton, TertiaryButton } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';
import * as Sentry from '@sentry/react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import Bowser from 'bowser';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { errorCodeTranslations } from '../errors';
import { DomainError } from './domainError';

const SNACK_BAR_KEY = 'debug-error-handler';

type GlobalErrorHandler = {
    onError: (options: {
        error: unknown;
        silent: boolean;
        /** Hides errors with the code `unknown` from the user. */
        silentUnknownError: boolean;
    }) => void;
};

/** Takes a 401 with code `unknown` as an expired session; returns whether it handled the error. */
function useSessionExpiryHandler(queryClient: QueryClient): (error: unknown) => boolean {
    return React.useCallback(
        (error: unknown) => {
            // A 401 naming its reason (`rfq.unauthorized`, …) refuses a valid token; the user needs that explanation.
            if (!(error instanceof HttpError) || error.status !== 401 || error.code !== 'unknown') {
                return false;
            }
            // `AuthenticatedContextProvider` renews the token and re-runs failed requests, or sends the user to login.
            // Joins a renewal in flight: a restart would wait on the SDK's lock, time out and keep the old token.
            queryClient.invalidateQueries({ queryKey: ACCESS_TOKEN_QUERY_KEY }, { cancelRefetch: false });
            // As exceptions these were the top issue (7,020 events, 207 users, 30 days); `http.error` still logs each.
            return true;
        },
        [queryClient],
    );
}

export function useGlobalErrorHandler(queryClient: QueryClient): GlobalErrorHandler {
    const showDebugErrorMessage = useShowDebugErrorMessage();
    const { enqueueSnackbar } = useSnackbar();
    const handleSessionExpiry = useSessionExpiryHandler(queryClient);

    const onError = React.useCallback(
        ({ error, silent, silentUnknownError }: { error: unknown; silent: boolean; silentUnknownError: boolean }) => {
            if (handleSessionExpiry(error)) {
                return;
            }

            const { known, message } = composeErrorMessage(error);

            if (error instanceof Error && !known) {
                logToExternalErrorHandlers(error);
            }

            if (!(error instanceof Error) && !known) {
                logToExternalErrorHandlers(new Error('An unknown error occurred: ' + JSON.stringify(error)));
            }

            const isUnknownAndShouldBeIgnored = silentUnknownError && !known;
            if ((isProductionEnvironment() || known) && !silent && !isUnknownAndShouldBeIgnored) {
                enqueueSnackbar(message, { key: message, variant: 'error', preventDuplicate: true });
            }
            if (!isProductionEnvironment()) {
                // No debug message on ci-e2e.dev.luminovo.ai, nor when silenced.
                if (!silent && !window.location.host.startsWith('ci-e2e.dev.luminovo.ai')) {
                    showDebugErrorMessage(error);
                } else {
                    // oxlint-disable-next-line
                    console.error(error);
                }
            }
        },
        [enqueueSnackbar, showDebugErrorMessage, handleSessionExpiry],
    );

    return { onError };
}

/** Outside production it also shows the error's stack trace. */
export function useDebugErrorHandler(): (error: unknown) => void {
    const showDebugErrorMessage = useShowDebugErrorMessage();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const handleSessionExpiry = useSessionExpiryHandler(queryClient);

    return (error: unknown) => {
        if (handleSessionExpiry(error)) {
            return;
        }

        const { known, message } = composeErrorMessage(error);

        if (error instanceof Error && !known) {
            logToExternalErrorHandlers(error);
        }

        if (error instanceof DomainError && known) {
            logToExternalErrorHandlers(
                new Error(error.code),
                {
                    extra: { context: error.context, message: error.message },
                },
                error.attachment,
            );
        }

        if (!(error instanceof Error) && !known) {
            logToExternalErrorHandlers(new Error('An unknown error occurred: ' + JSON.stringify(error)));
        }

        if (isProductionEnvironment() || known) {
            enqueueSnackbar(message, { key: message, variant: 'error', preventDuplicate: true });
        }

        if (!isProductionEnvironment()) {
            showDebugErrorMessage(error);
        }
    };
}

function composeErrorMessage(error: unknown) {
    const span = Sentry.getActiveSpan();
    if (error instanceof HttpError) {
        span?.setAttributes({
            'errorHandler.type': 'HttpError',
            'errorHandler.message': error.message,
            'errorHandler.status': String(error.status),
            'errorHandler.code': error.code,
        });

        if (error.status === 403) {
            if (error.endpoint.includes('/comments')) {
                return {
                    known: true,
                    message: t`You are not allowed to view comments.`,
                };
            }

            return {
                known: true,
                message: t`Sorry, you are not authorised to perform this action. Please contact our customer support if you need assistance.`,
            };
        }

        if (!Boolean(errorCodeTranslations[error.code]) && error.code !== 'unknown') {
            logToExternalErrorHandlers(new Error(`Error code missing translation: ${String(error.code)}`), {
                // Per code: all are reported from this one line, so they would otherwise share one Sentry issue.
                fingerprint: ['missing-error-code-translation', error.code],
            });
        }

        return {
            known: Boolean(errorCodeTranslations[error.code]) && error.code !== 'unknown',
            message: transEnum(error.code, errorCodeTranslations),
        };
    }

    if (error instanceof DomainError) {
        span?.setAttributes({
            'errorHandler.type': 'DomainError',
            'errorHandler.message': error.message,
            'errorHandler.code': error.code,
        });
        return {
            known: true,
            message: error.message,
        };
    }

    span?.setAttributes({
        'errorHandler.type': 'Unknown',
        'errorHandler.message': String(error),
    });

    return {
        known: false,
        message: t`Sorry, there was a problem. Please try again later or contact our customer support.`,
    };
}

function useShowDebugErrorMessage() {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    return React.useCallback(
        (error: unknown) => {
            if (!(error instanceof Error)) {
                // oxlint-disable-next-line
                console.error(error);
                return;
            }

            enqueueSnackbar('', {
                key: SNACK_BAR_KEY,
                preventDuplicate: true,
                content: (
                    <div
                        style={{
                            marginTop: '24px',
                            padding: '16px',
                            borderRadius: '8px',
                            background: '#FFF0F1',
                            color: '#FF001F',
                            fontSize: '16px',
                            overflow: 'auto',
                            width: 'calc(100vw - 72px)',
                            height: 'calc(60vh - 72px)',
                            boxShadow: `0.3px 0.5px 0.7px hsl(0deg 0% 63% / 0.36),
                                0.8px 1.6px 2px -0.8px hsl(0deg 0% 63% / 0.36),
                                2.1px 4.1px 5.2px -1.7px hsl(0deg 0% 63% / 0.36),
                                5px 10px 12.6px -2.5px hsl(0deg 0% 63% / 0.36)`,
                        }}
                    >
                        <Flexbox gap={8} style={{ marginBottom: '16px' }}>
                            <PrimaryButton onClick={() => closeSnackbar(SNACK_BAR_KEY)}>Close</PrimaryButton>
                            <TertiaryButton onClick={() => navigator.clipboard.writeText(error.stack || error.message)}>
                                Copy Stack
                            </TertiaryButton>
                        </Flexbox>
                        {(() => {
                            const browser = Bowser.getParser(window.navigator.userAgent);
                            const isChrome = browser.getBrowserName() === 'Chrome';

                            if (isChrome && error.stack) {
                                // Chrome's stack traces have clickable links.
                                return (
                                    <pre
                                        style={{
                                            fontSize: '12px',
                                            overflow: 'auto',
                                            maxHeight: '400px',
                                        }}
                                    >
                                        {error.stack}
                                    </pre>
                                );
                            } else {
                                // Firefox's and Safari's stack traces are poor, so show the formatted error instead.
                                return (
                                    <div>
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>{error.name || 'Error'}: </strong>
                                            <span>{error.message}</span>
                                        </div>
                                        {error.stack && (
                                            <details style={{ marginTop: '8px' }}>
                                                <summary style={{ cursor: 'pointer', fontSize: '12px' }}>
                                                    Show raw stack trace
                                                </summary>
                                                <pre
                                                    style={{
                                                        fontSize: '11px',
                                                        overflow: 'auto',
                                                        maxHeight: '200px',
                                                        marginTop: '8px',
                                                        backgroundColor: '#f5f5f5',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                    }}
                                                >
                                                    {error.stack}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                );
                            }
                        })()}
                    </div>
                ),
                autoHideDuration: null,
            });
        },
        [enqueueSnackbar, closeSnackbar],
    );
}
